import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useConnectionState, useLocalParticipant } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import {
  Box, Button, ButtonText, Center, Heading, HStack, Icon, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { Clock } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import {
  OralExamErrorState,
  OralExamPollReconnectingBanner,
} from '@/common/components/oralExam/OralExamErrorState';
import { SessionVoidTechnicalScreen } from '@/common/components/oralExam/SessionVoidTechnicalScreen';
import { MeetingSessionShell } from '@/common/components/meeting/MeetingSessionShell';
import {
  MeetingOpenElsewhereProvider,
  useMeetingOpenElsewhere,
} from '@/common/components/meeting/MeetingOpenElsewhere';
import { useMeetingConnectionState } from '@/common/components/meeting/MeetingConnectionGuard';
import { MeetingStage } from '@/common/components/meeting/MeetingStage';
import { useMeetingLobbyState } from '@/common/components/meeting/useMeetingLobbyState';
import { useLiveKitConnectWatchdog } from '@/common/hooks/oralExam/useLiveKitConnectWatchdog';
import { useLiveKitTokenRequest } from '@/common/hooks/oralExam/useLiveKitTokenRequest';
import { CarriedScreenShareProvider, useCarriedScreenShare } from '@/student/module/hooks/useCarriedScreenShare';
import {
  CarriedJoinSettingsProvider,
  useCarriedJoinSettings,
} from '@/student/module/hooks/useCarriedJoinSettings';
import { useAuthStore } from '@/common/auth/store';
import { useEnterOralExamPracticeLobby } from '@/common/hooks/oralExam/useEnterOralExamPracticeLobby';
import { useOralExamMutations } from '@/common/hooks/oralExam/useOralExamMutations';
import { getOralExamApiErrorCode, getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import { AxiosError } from 'axios';
import { useOralExamRunStatePoll } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import { useGetModuleByCourseAndId } from '@/common/api/endpoints/modules/modules';
import { useCompleteModuleSubmissionUx } from '@/common/hooks/student/module/useCompleteModuleSubmissionUx';
import { getJoinErrorCode } from '@/common/utils/oralExam/joinErrorCode';
import { clearCachedJoinCreds } from '@/common/components/meeting/meetingSessionPersistence';
import { getLiveKitJoinCacheKey } from '@/common/utils/oralExam/liveKitJoin';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import { OralExamMode } from '@/common/api/models/oralExamMode';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { OralExamOperationalFault } from '@/common/api/models/oralExamOperationalFault';
import {
  AgentUnavailableTerminalScreen,
  shouldShowAgentUnavailableTerminal,
} from '@/common/components/oralExam/AgentUnavailableTerminalScreen';
import type { LiveKitJoinHandle } from '@/student/module/hooks/useEventTaskSlots';
import {
  isSelfServiceRun,
  runRequiresEvaluation,
  usesGroupLounge,
  usesOrientationFilm,
} from '@/common/utils/oralExam/runConduct';
import { PeerGoneBanner } from '@/common/components/oralExam/PeerGoneBanner';
import { useOneToOneRemotePeerPresent } from '@/common/hooks/oralExam/useOneToOneRemotePeerPresent';
import { useCompleteIfAbsentOnPeerGoneClose } from '@/common/hooks/oralExam/useCompleteIfAbsentOnPeerGoneClose';
import { usePeerGoneClosingCountdown } from '@/common/hooks/oralExam/usePeerGoneClosingCountdown';
import {
  isAtLastOralExamQuestion,
  isOneToOnePeerGone,
  shouldShowOneToOnePeerGoneBanner,
} from '@/common/utils/oralExam/oneToOnePeerGone';

import { OralExamLiveView } from './OralExamLiveView';
import { StudentSessionExpiredScreen } from './StudentSessionExpiredScreen';
import { ExamWrapUp, type ExamWrapUpProps } from './ExamWrapUp';
import { OralExamLobby } from './lobby/OralExamLobby';
import { StudentExamControlBar } from './StudentExamControlBar';
import { StudentGroupBriefingScreen } from './StudentGroupBriefingScreen';
import {
  ORAL_EXAM_CONSENT_POLICY_VERSION,
  ORAL_EXAM_CONSENT_SCOPE,
  ORAL_EXAM_CONDUCT_POLICY_VERSION,
} from './preflight/OralExamPreFlightSteps';
import { useStudentSupervisorFrame } from '../utils/useStudentSupervisorFrame';

import { deriveStudentRuntimeJourney, shouldUseStudentExamShell } from '../utils/deriveStudentRuntimeJourney';
import { EMPTY_ORAL_EXAM_LOUNGE, useOralExamLounge } from '../utils/oralExamLounge';
import {
  WaitingBedMuteContext,
  WaitingLoungeAtmosphereChrome,
} from './lobby/WaitingLoungeAtmosphereChrome';
import {
  PREFLIGHT_LEAVE_OVERLAY_MS,
  useWaitingLoungeAtmosphere,
} from './lobby/useWaitingLoungeAtmosphere';
import type { LobbyStepKey } from './lobby/types';

const ORIENTATION_STAMP_MAX_ATTEMPTS = 3;
const ORIENTATION_STAMP_RETRY_MS = 2000;

const delayMs = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms);
});

type ConnectionAwareOralExamLiveViewProps = Omit<
React.ComponentProps<typeof OralExamLiveView>,
'isReconnecting' | 'studentFirstName' | 'onStartExam' | 'isMarkingStudentReady' | 'startExamError'
| 'studentDoneError'
> & {
  studentFirstName: string;
  onStartExam: () => void;
  isMarkingStudentReady: boolean;
  startExamError: string | null;
  studentDoneError: string | null;
};

/** Reads LiveKit hooks inside the room provider and passes derived journey down. */
function ConnectionAwareOralExamLiveView({
  runState,
  studentFirstName,
  onStartExam,
  isMarkingStudentReady,
  startExamError,
  studentDoneError,
  ...rest
}: ConnectionAwareOralExamLiveViewProps) {
  const { isReconnecting: meetingReconnecting } = useMeetingConnectionState();
  const connectionFromHook = useConnectionState();
  const isReconnecting = meetingReconnecting
    || connectionFromHook === ConnectionState.Reconnecting
    || connectionFromHook === ConnectionState.SignalReconnecting;

  const runtimeJourney = useMemo(
    () => deriveStudentRuntimeJourney({
      sessionPhase: runState.sessionPhase,
      runState: runState.state,
      conductPhase: runState.conductPhase,
      connectionState: connectionFromHook,
      isReconnecting,
    }),
    [
      connectionFromHook,
      isReconnecting,
      runState.conductPhase,
      runState.sessionPhase,
      runState.state,
    ],
  );

  return (
    <OralExamLiveView
      {...rest}
      runState={runState}
      studentFirstName={studentFirstName}
      onStartExam={onStartExam}
      isMarkingStudentReady={isMarkingStudentReady}
      startExamError={startExamError}
      studentDoneError={studentDoneError}
      isReconnecting={isReconnecting}
      liveKitConnectionState={connectionFromHook}
      runtimeJourney={runtimeJourney}
    />
  );
}

/** Reads LiveKit connect watchdog inside the room and gates session content. */
export function LiveKitConnectAwareSessionBody({
  sessionBody,
  isRunStatePollError,
  runState,
  onBack,
  onRejoin,
}: {
  sessionBody: React.ReactNode;
  isRunStatePollError: boolean;
  runState: OralExamRunStateResponse;
  onBack: () => void;
  onRejoin: () => void;
}) {
  const { t } = useTranslation();
  const { connectFailed } = useLiveKitConnectWatchdog();

  if (connectFailed) {
    return (
      <OralExamErrorState
        testID="oral-exam-livekit-connect-error"
        title={t('oralExam.errorState.liveKitConnectFailedTitle', {
          defaultValue: 'Could not connect to the exam room',
        })}
        message={t('oralExam.errorState.liveKitConnectFailedBody', {
          defaultValue: 'We could not establish a live connection. Try again or leave and rejoin from your bookings.',
        })}
        retry={onRejoin}
        leave={onBack}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }

  return (
    <VStack flex={1} space="md">
      {isRunStatePollError && runState ? <OralExamPollReconnectingBanner /> : null}
      {sessionBody}
    </VStack>
  );
}

/** In-room body + control bar; must render under MeetingSessionShell (LiveKit hooks). */
function OralExamInRoomSession({
  runState,
  useMeetingStageSpeakerRail,
  oralExamLiveView,
  isTerminal,
  isWaiting,
  onBack,
  handleAbortExam,
  isAborting,
  completeIfAbsent,
}: {
  runState: OralExamRunStateResponse;
  useMeetingStageSpeakerRail: boolean;
  oralExamLiveView: React.ReactNode;
  isTerminal: boolean;
  isWaiting: boolean;
  onBack: () => void;
  handleAbortExam: () => void | Promise<void>;
  isAborting: boolean;
  completeIfAbsent: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const { isReconnecting: meetingReconnecting } = useMeetingConnectionState();
  // Practice never remounts into a new room, and the waiting film turns the
  // microphone off. This exam view is what turns it back on.
  useEffect(() => {
    localParticipant.setMicrophoneEnabled(true).catch(() => undefined);
  }, [localParticipant]);
  const connectionFromHook = useConnectionState();
  const isConnectionReconnecting = meetingReconnecting
    || connectionFromHook === ConnectionState.Reconnecting
    || connectionFromHook === ConnectionState.SignalReconnecting;
  const runtimeJourneyForControls = useMemo(
    () => deriveStudentRuntimeJourney({
      sessionPhase: runState.sessionPhase,
      runState: runState.state,
      conductPhase: runState.conductPhase,
      connectionState: connectionFromHook,
      isReconnecting: isConnectionReconnecting,
    }),
    [
      connectionFromHook,
      isConnectionReconnecting,
      runState.conductPhase,
      runState.sessionPhase,
      runState.state,
    ],
  );
  const shellHasIntegratedFooter = runState.state === 'in_progress'
    && shouldUseStudentExamShell(runState, runtimeJourneyForControls);
  const peerGoneBannerEnabled = shouldShowOneToOnePeerGoneBanner(runState);
  const remotePeerPresent = useOneToOneRemotePeerPresent();
  // LiveKit identity gone OR poll stamp (missed participant_left). Rejoin clears the
  // stamp server-side on participant_joined so present + no stamp → no banner.
  const peerGone = peerGoneBannerEnabled && isOneToOnePeerGone(runState, remotePeerPresent);
  const isLastQuestion = isAtLastOralExamQuestion(runState);
  const { secondsRemaining, showClosingCountdown } = usePeerGoneClosingCountdown(
    peerGone,
    isLastQuestion,
  );
  const {
    error: completeIfAbsentError,
    isCompleting: isCompletingIfAbsent,
    retry: retryCompleteIfAbsent,
  } = useCompleteIfAbsentOnPeerGoneClose({
    showClosingCountdown,
    secondsRemaining,
    completeIfAbsent,
    defaultErrorMessage: t('oralExam.peerGone.completeFailed', {
      defaultValue: 'Could not close the exam. Try again.',
    }),
  });

  return (
    <VStack flex={1} space="md" p="$4" overflow="hidden">
      {peerGone ? (
        <PeerGoneBanner
          variant="student"
          showClosingCountdown={showClosingCountdown}
          secondsRemaining={secondsRemaining}
          errorMessage={completeIfAbsentError}
          onRetry={() => { retryCompleteIfAbsent().catch(() => undefined); }}
          isRetrying={isCompletingIfAbsent}
        />
      ) : null}
      {useMeetingStageSpeakerRail
        ? (
          <MeetingStage
            content={oralExamLiveView}
            contentPlacement="side"
            speakerAspectRatio={4 / 3}
            speakerMaxWidth={440}
          />
        )
        : oralExamLiveView}
      {!isTerminal && !shellHasIntegratedFooter ? (
        <StudentExamControlBar
          onAbort={handleAbortExam}
          isAborting={isAborting}
          onLeave={isWaiting ? onBack : undefined}
          requiresEvaluation={runRequiresEvaluation(runState)}
          committed={runState.committed ?? false}
        />
      ) : null}
    </VStack>
  );
}

export type OralExamLiveSessionProps = {
  ids: OralExamRouteIds;
  liveKitJoin: LiveKitJoinHandle;
  onBack: () => void;
  /** Advertised slot start (T). Shown as a clock on the waiting hold. */
  slotStartIso?: string | null;
  /** Booking slot id for preflight/briefing lecturer identity (not parsed from room name). */
  slotId?: string;
};

function isTooEarlyError(err: unknown): boolean {
  return getJoinErrorCode(err) === 'too_early';
}

function isLateJoinCutoffError(err: unknown): boolean {
  return getJoinErrorCode(err) === 'late_join_cutoff';
}

function isSessionStartedWithoutYouError(err: unknown): boolean {
  return getJoinErrorCode(err) === 'session_started_without_you';
}

function isExpiredJoinError(err: unknown): boolean {
  return getJoinErrorCode(err) === 'expired';
}

function isSessionAlreadyEndedJoinError(err: unknown): boolean {
  return getJoinErrorCode(err) === 'session_already_ended';
}

function getRetryAfterSeconds(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const obj = err as Record<string, unknown>;
  const directVal = (obj.details as Record<string, unknown> | undefined)?.retry_after_seconds;
  if (typeof directVal === 'number') return directVal;
  const errorDetails = ((obj?.response as Record<string, unknown>)
    ?.data as Record<string, unknown>)
    ?.error as Record<string, unknown> | undefined;
  const nestedVal = errorDetails?.details as Record<string, unknown> | undefined;
  const val = nestedVal?.retry_after_seconds;
  return typeof val === 'number' ? val : undefined;
}

function formatDuration(seconds: number, t: (k: string, opts?: Record<string, unknown>) => string): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);

  if (days >= 1) {
    return hours > 0
      ? t('oralExam.tooEarly.durationDaysHours', { days, hours })
      : t('oralExam.tooEarly.durationDays', { days });
  }
  if (hours >= 1) {
    return minutes > 0
      ? t('oralExam.tooEarly.durationHoursMinutes', { hours, minutes })
      : t('oralExam.tooEarly.durationHours', { hours });
  }
  if (minutes > 1) return t('oralExam.tooEarly.durationMinutes', { minutes });
  return t('oralExam.tooEarly.durationSoon');
}

function PreflightWaitingAtmosphere({
  bedActive,
  muteUplink,
  waitingBedUrl,
  unload,
  showLeavingOverlay,
  hostMuteInBanner,
  children,
}: {
  bedActive: boolean;
  muteUplink: boolean;
  waitingBedUrl: string | null | undefined;
  unload: boolean;
  showLeavingOverlay: boolean;
  hostMuteInBanner: boolean;
  children: React.ReactNode;
}) {
  const atmosphere = useWaitingLoungeAtmosphere({
    active: bedActive,
    muteUplink,
    waitingBedUrl,
    unload,
    showLeavingOverlay,
  });
  const muteControls = useMemo(() => ({
    show: atmosphere.showBedMute,
    bedMuted: atmosphere.bedMuted,
    onToggle: atmosphere.toggleBedMute,
  }), [
    atmosphere.bedMuted,
    atmosphere.showBedMute,
    atmosphere.toggleBedMute,
  ]);

  return (
    <WaitingBedMuteContext.Provider value={muteControls}>
      <Box flex={1} position="relative">
        <WaitingLoungeAtmosphereChrome
          showBedMute={atmosphere.showBedMute}
          bedMuted={atmosphere.bedMuted}
          onToggleBedMute={atmosphere.toggleBedMute}
          overlayVisible={atmosphere.overlayVisible}
          hostMuteInBanner={hostMuteInBanner}
        />
        {children}
      </Box>
    </WaitingBedMuteContext.Provider>
  );
}

function TooEarlyScreen({ seconds, onBack }: { seconds?: number; onBack: () => void }) {
  const { t } = useTranslation();
  const durationLabel = seconds != null ? formatDuration(seconds, t) : null;

  return (
    <Center flex={1} bg="$bgApp" p="$8">
      <VStack space="xl" alignItems="center" maxWidth={380}>
        <Box
          w={80}
          h={80}
          borderRadius="$full"
          bg="$backgroundLight100"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={Clock} size="xl" color="$textHint" />
        </Box>

        <VStack space="md" alignItems="center">
          <Heading size="xl" textAlign="center" color="$textHeading">
            {t('oralExam.tooEarly.title')}
          </Heading>
          <Text size="md" color="$textSubtle" textAlign="center" lineHeight={24}>
            {durationLabel
              ? t('oralExam.tooEarly.bodyWithDuration', { duration: durationLabel })
              : t('oralExam.tooEarly.body')}
          </Text>
        </VStack>

        <Button variant="outline" size="lg" onPress={onBack} mt="$4">
          <ButtonText>{t('oralExam.tooEarly.back')}</ButtonText>
        </Button>
      </VStack>
    </Center>
  );
}

function OralExamLiveSessionInner({
  ids, liveKitJoin, onBack, slotStartIso, slotId,
}: OralExamLiveSessionProps) {
  const { t } = useTranslation();
  const studentFirstName = useAuthStore((s) => s.user?.firstname ?? '');
  const {
    enterPracticeLobby,
    isEnteringPractice,
    enterPracticeError,
  } = useEnterOralExamPracticeLobby(ids.taskId);
  const completeModuleSubmissionUx = useCompleteModuleSubmissionUx();
  const { data: moduleResponse } = useGetModuleByCourseAndId(ids.courseId, ids.moduleId, undefined, {
    query: { enabled: Boolean(ids.courseId && ids.moduleId) },
  });
  const moduleTitle = moduleResponse?.data?.data?.title ?? '';
  const [startExamError, setStartExamError] = useState<string | null>(null);
  const [startPracticeError, setStartPracticeError] = useState<string | null>(null);
  const [studentDoneError, setStudentDoneError] = useState<string | null>(null);
  const [lobbyActiveStep, setLobbyActiveStep] = useState<LobbyStepKey | null>(null);
  const [isCompletingOrientation, setIsCompletingOrientation] = useState(false);
  const [orientationStampRetrying, setOrientationStampRetrying] = useState(false);
  const [orientationStampExhausted, setOrientationStampExhausted] = useState(false);
  const [showPreflightLeaveOverlay, setShowPreflightLeaveOverlay] = useState(false);
  const orientationStampInFlightRef = useRef(false);
  const {
    joinCreds,
    joinError,
    refetch,
    retry,
    isLoading,
    clearInSessionTokenRetryWait,
  } = useLiveKitTokenRequest(liveKitJoin);
  const {
    openElsewhere,
    useThisWindow,
    sessionGeneration,
  } = useMeetingOpenElsewhere();
  const { release: releaseCarriedScreenShare } = useCarriedScreenShare();
  const { mediaGranted } = useCarriedJoinSettings();
  const leaveSession = useCallback(() => {
    releaseCarriedScreenShare();
    const cacheKey = getLiveKitJoinCacheKey(liveKitJoin);
    if (cacheKey) clearCachedJoinCreds(cacheKey);
    onBack();
  }, [liveKitJoin, onBack, releaseCarriedScreenShare]);
  // Oral exam uses its own unified Lobby (device → consent → screen → ID →
  // waiting), so the generic "Get ready to join" MeetingLobby is skipped.
  const { lobbyDone, joinSettings, onLobbyJoin } = useMeetingLobbyState({
    initialLobbyDone: true,
    initialJoinSettings: { microphoneEnabled: false, cameraEnabled: false },
  });
  // Do not poll before the first token (lobby tests / complete-if-absent
  // double-fire). After join, keep polling if the token later drops (void
  // cancels the booking and remint raises session_already_ended).
  const keepPollingAfterJoinRef = useRef(false);
  if (joinCreds) {
    keepPollingAfterJoinRef.current = true;
  }
  const pollEnabled = Boolean(lobbyDone && (joinCreds || keepPollingAfterJoinRef.current));

  const {
    runState,
    etag,
    queryKey,
    isError: isRunStatePollError,
    refetch: refetchRunState,
  } = useOralExamRunStatePoll(ids, {
    enabled: pollEnabled,
    polling: pollEnabled,
  });
  // Graded pre-flight and the group briefing share one room with the supervisors.
  // The room publishes the microphone at connect and does not take it back when
  // this flag later flips. Permission is often granted before the phase poll
  // returns, so an unknown phase must stay off. A dry run is its own room and
  // may publish once that phase is known. The exam room connects with it on.
  const phaseKnown = Boolean(runState);
  const suppressSharedRoomMic = Boolean(
    runState
    && !isSelfServiceRun(runState)
    && (
      runState.sessionPhase === OralExamSessionPhase.pre_flight
      || runState.sessionPhase === OralExamSessionPhase.group_briefing
    ),
  );
  const effectiveJoinSettings = useMemo(
    () => (mediaGranted
      ? {
        ...joinSettings,
        microphoneEnabled: phaseKnown && !suppressSharedRoomMic,
        cameraEnabled: true,
      }
      : joinSettings),
    [joinSettings, mediaGranted, phaseKnown, suppressSharedRoomMic],
  );

  const {
    signalStudentDone,
    requestHelp,
    recordConsent,
    acknowledgeConduct,
    submitIdentityPhotos,
    abortExam,
    completeIfAbsent,
    leavePreflight,
    markPreflightLobbyReady,
    markStudentReady,
    markOrientationCompleted,
    startSelfServiceSession,
    openAnswerWindow,
    isSignalingDone,
    isRequestingHelp,
    isRecordingConsent,
    isAcknowledgingConduct,
    isSubmittingIdentityPhotos,
    isAborting,
    isMarkingStudentReady,
    isStartingSelfService,
  } = useOralExamMutations(ids, { etag, stateQueryKey: queryKey });

  const filmEnabled = usesOrientationFilm(runState);
  const groupLoungeEnabled = usesGroupLounge(runState);
  const idVerificationStatus = runState?.idVerification?.status;
  const loungeFetchEnabled = Boolean(
    joinCreds
    && lobbyDone
    && filmEnabled
    && runState?.sessionPhase === OralExamSessionPhase.pre_flight
    && (idVerificationStatus === 'submitted'
      || idVerificationStatus === 'approved'
      || idVerificationStatus === 'rejected'),
  );
  const { data: loungeData, isSuccess: loungeFetchSucceeded, isError: loungeFetchFailed } = useOralExamLounge(ids, loungeFetchEnabled);
  const loungePayload = loungeFetchSucceeded && loungeData ? loungeData : EMPTY_ORAL_EXAM_LOUNGE;
  const loungeOrientationResolved = loungeFetchEnabled && loungeFetchSucceeded;
  const orientationVideoUrlForLobby = !loungeFetchEnabled
    ? undefined
    : loungeFetchSucceeded
      ? loungePayload.orientationVideoUrl
      : undefined;

  const markOrientationCompletedRef = useRef(markOrientationCompleted);
  markOrientationCompletedRef.current = markOrientationCompleted;
  const orientationCompletedAtRef = useRef(runState?.orientationCompletedAt);
  orientationCompletedAtRef.current = runState?.orientationCompletedAt;

  const handleOrientationComplete = useCallback(async () => {
    if (
      orientationStampInFlightRef.current
      || orientationCompletedAtRef.current
    ) {
      return;
    }
    orientationStampInFlightRef.current = true;
    setIsCompletingOrientation(true);
    setOrientationStampExhausted(false);
    try {
      const stampWithRetry = async (
        attempt: number,
      ): Promise<'success' | 'exhausted' | 'stopped'> => {
        try {
          await markOrientationCompletedRef.current();
          return 'success';
        } catch (error) {
          const status = error instanceof AxiosError ? error.response?.status : undefined;
          const code = getOralExamApiErrorCode(error);
          if (status === 403 || code === 'session_already_ended') {
            return 'stopped';
          }
          if (attempt >= ORIENTATION_STAMP_MAX_ATTEMPTS) {
            return 'exhausted';
          }
          setOrientationStampRetrying(true);
          await delayMs(ORIENTATION_STAMP_RETRY_MS);
          setOrientationStampRetrying(false);
          return stampWithRetry(attempt + 1);
        }
      };
      const stampResult = await stampWithRetry(1);
      setOrientationStampRetrying(false);
      if (stampResult === 'success') {
        setOrientationStampExhausted(false);
      } else if (stampResult === 'exhausted') {
        setOrientationStampExhausted(true);
      }
    } finally {
      orientationStampInFlightRef.current = false;
      setIsCompletingOrientation(false);
      setOrientationStampRetrying(false);
    }
  }, []);

  const handleStartExam = useCallback(async () => {
    if (isMarkingStudentReady || runState?.studentReadyAt) return;
    setStartExamError(null);
    try {
      await markStudentReady();
    } catch {
      setStartExamError(t('oralExam.student.examShell.startFailed', {
        defaultValue: 'Could not start the exam. Try again.',
      }));
    }
  }, [isMarkingStudentReady, markStudentReady, runState?.studentReadyAt, t]);

  const handleStartPractice = useCallback(async () => {
    if (isStartingSelfService) return;
    setStartPracticeError(null);
    try {
      await startSelfServiceSession();
    } catch (error) {
      setStartPracticeError(getOralExamApiErrorMessage(
        error,
        t('oralExam.practice.startFailed', {
          defaultValue: 'Could not start the dry run. Try again.',
        }),
      ));
    }
  }, [isStartingSelfService, startSelfServiceSession, t]);

  const handleStudentDone = useCallback(async () => {
    if (isSignalingDone || runState?.studentDoneAt) return;
    setStudentDoneError(null);
    try {
      await signalStudentDone();
    } catch (error) {
      setStudentDoneError(getOralExamApiErrorMessage(
        error,
        t('oralExam.actionFailed', { defaultValue: 'Action failed' }),
      ));
    }
  }, [isSignalingDone, runState?.studentDoneAt, signalStudentDone, t]);

  const handleOpenAnswerWindow = useCallback(async () => {
    if (runState?.questionDeadline) return;
    await openAnswerWindow();
  }, [openAnswerWindow, runState?.questionDeadline]);

  const handleAbortExam = useCallback(async () => {
    try {
      await abortExam();
      leaveSession();
    } catch {
      // Error toast is surfaced by useOralExamMutations; stay in the session.
    }
  }, [abortExam, leaveSession]);

  // Leaving the pre-flight lobby: document the departure server-side (clears
  // pending consent/ID capture so the lecturer roster un-sticks) *before*
  // navigating away. Best-effort — always navigates, even if the reset call
  // fails, so the student is never trapped in the room.
  const handleLeavePreflight = useCallback(async () => {
    await leavePreflight();
    leaveSession();
  }, [leavePreflight, leaveSession]);

  const handleCompletedWrapUpDone = useCallback(() => {
    if (!runState || isSelfServiceRun(runState) || !runRequiresEvaluation(runState)) {
      leaveSession();
      return;
    }
    completeModuleSubmissionUx({
      moduleTitle,
      courseId: ids.courseId,
      moduleId: ids.moduleId,
    });
  }, [
    completeModuleSubmissionUx,
    leaveSession,
    moduleTitle,
    ids.courseId,
    ids.moduleId,
    runState,
  ]);

  const handleWrapUpDone = useCallback((wrapUpState: ExamWrapUpProps['state']) => {
    if (wrapUpState === 'completed') {
      handleCompletedWrapUpDone();
      return;
    }
    leaveSession();
  }, [handleCompletedWrapUpDone, leaveSession]);

  const {
    supervisorName: frameSupervisorName,
    supervisorPhotoUrl: frameSupervisorPhotoUrl,
    supervisorFaces,
    visitingLecturerId,
  } = useStudentSupervisorFrame(ids, runState);

  const isTerminalRunState = runState?.state === 'completed' || runState?.state === 'aborted';
  const shouldLeaveMeeting = Boolean(
    runState?.sessionPhase === OralExamSessionPhase.post_exam
    || isTerminalRunState,
  );

  useEffect(() => {
    if (shouldLeaveMeeting) {
      releaseCarriedScreenShare();
    }
  }, [releaseCarriedScreenShare, shouldLeaveMeeting]);

  // When the cohort moves to official, the backend routes token minting to the
  // per-booking exam room (student_uses_preflight_room → false). Re-mint so
  // MeetingSessionShell remounts MeetingRoom when roomName changes.
  // Capacity-1 skips group_briefing (pre_flight → in_exam); that path must
  // refetch too — otherwise the student stays on the preflight room token.
  // Dry-run never uses the preflight room (practice kind mints the exam room
  // from lobby-ready). Reminting here only rotates the JWT.
  const prevSessionPhaseRef = useRef<string | undefined>(undefined);
  const prevPreflightPhaseRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const phase = runState?.sessionPhase;
    const prev = prevSessionPhaseRef.current;
    if (
      prev
      && prev !== OralExamSessionPhase.in_exam
      && phase === OralExamSessionPhase.in_exam
    ) {
      // Self-service (dry-run) already sits in the exam room from lobby-ready.
      // Reminting here only rotates the JWT and used to drop the LiveKit
      // connection (Connection lost). Graded preflight → exam still remints.
      if (!isSelfServiceRun(runState)) {
        refetch();
      }
    }
    prevSessionPhaseRef.current = phase;
  }, [runState, refetch]);

  useEffect(() => {
    const phase = runState?.sessionPhase;
    const prev = prevPreflightPhaseRef.current;
    if (
      prev === OralExamSessionPhase.pre_flight
      && phase === OralExamSessionPhase.group_briefing
    ) {
      setShowPreflightLeaveOverlay(true);
      const timer = setTimeout(() => {
        setShowPreflightLeaveOverlay(false);
        prevPreflightPhaseRef.current = phase;
      }, PREFLIGHT_LEAVE_OVERLAY_MS);
      return () => clearTimeout(timer);
    }
    if (phase !== OralExamSessionPhase.group_briefing) {
      prevPreflightPhaseRef.current = phase;
    }
    return undefined;
  }, [runState?.sessionPhase]);

  if (runState?.state === 'aborted' && runState.endReason === 'session_void') {
    return (
      <SessionVoidTechnicalScreen
        onLeave={leaveSession}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }

  if (runState?.bookingCancellationReason === 'student_no_show') {
    const wasPresentInLobby = Boolean(runState.firstSeenAt);
    return (
      <OralExamErrorState
        title={
          wasPresentInLobby
            ? t('oralExam.booking.examNotReadyAtStartTitle', {
              defaultValue: 'You were not ready when the exam started',
            })
            : t('oralExam.booking.examStartedWithoutYou')
        }
        message={
          wasPresentInLobby
            ? t('oralExam.booking.examNotReadyAtStartHint', {
              defaultValue: 'You were not ready when the exam started. This attempt counts and there is no grade.',
            })
            : t('oralExam.booking.examStartedWithoutYouHint')
        }
        tone="warning"
        leave={leaveSession}
        leaveLabel={t('oralExam.tooEarly.back')}
        testID={wasPresentInLobby ? 'oral-exam-not-ready-at-start' : undefined}
      />
    );
  }

  if (
    runState?.sessionPhase === OralExamSessionPhase.pre_flight
    && Boolean(runState.brieferId)
  ) {
    return (
      <OralExamErrorState
        title={t('oralExam.booking.briefingStartedWithoutYouTitle', {
          defaultValue: 'This sitting is starting without you',
        })}
        message={t('oralExam.booking.briefingStartedWithoutYouHint', {
          defaultValue: 'This sitting is starting without you because your check-in was not accepted. This attempt counts.',
        })}
        tone="warning"
        leave={leaveSession}
        leaveLabel={t('oralExam.tooEarly.back')}
        testID="oral-exam-briefing-started-without-you"
      />
    );
  }

  if (
    runState?.bookingCancellationReason === 'lecturer_no_show'
    || runState?.bookingCancellationReason === 'platform_failure'
  ) {
    return (
      <OralExamErrorState
        title={t('oralExam.booking.universityDifficultyTitle')}
        message={t('oralExam.booking.universityDifficultyHint')}
        tone="warning"
        leave={leaveSession}
        leaveLabel={t('oralExam.tooEarly.back')}
        testID="oral-exam-university-difficulty"
      />
    );
  }

  if (joinError) {
    if (isTooEarlyError(joinError)) {
      return <TooEarlyScreen seconds={getRetryAfterSeconds(joinError)} onBack={leaveSession} />;
    }
    if (isLateJoinCutoffError(joinError)) {
      return (
        <OralExamErrorState
          title={t('oralExam.booking.examStartedLocked')}
          message={t('oralExam.booking.examStartedLockedHint')}
          tone="warning"
          leave={leaveSession}
          leaveLabel={t('oralExam.tooEarly.back')}
        />
      );
    }
    if (isSessionStartedWithoutYouError(joinError)) {
      return (
        <OralExamErrorState
          title={t('oralExam.booking.examStartedWithoutYou')}
          message={t('oralExam.booking.examStartedWithoutYouHint')}
          tone="warning"
          leave={leaveSession}
          leaveLabel={t('oralExam.tooEarly.back')}
        />
      );
    }
    if (isExpiredJoinError(joinError)) {
      return (
        <OralExamErrorState
          title={t('oralExam.booking.examMissed')}
          message={t('oralExam.booking.examMissedHint')}
          tone="warning"
          leave={leaveSession}
          leaveLabel={t('oralExam.tooEarly.back')}
        />
      );
    }
    if (isSessionAlreadyEndedJoinError(joinError)) {
      return (
        <OralExamErrorState
          title={t('oralExam.booking.sessionAlreadyEnded', {
            defaultValue: 'You already completed this session',
          })}
          message={t('oralExam.booking.sessionAlreadyEndedHint', {
            defaultValue: 'This oral exam session is finished. Book a new appointment if you need another attempt.',
          })}
          leave={leaveSession}
          leaveLabel={t('oralExam.tooEarly.back')}
        />
      );
    }
    return (
      <OralExamErrorState
        title={t('oralExam.errorState.joinFailedTitle', {
          defaultValue: 'Could not join the exam room',
        })}
        message={t('oralExam.joinFailed')}
        retry={retry}
        leave={leaveSession}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }

  if (!joinCreds && isLoading) {
    return (
      <Center flex={1} p="$4">
        <Spinner size="large" />
        <Text size="sm" color="$textSubtle" mt="$2">{t('oralExam.connecting')}</Text>
      </Center>
    );
  }

  if (!joinCreds) {
    return (
      <OralExamErrorState
        title={t('oralExam.errorState.joinFailedTitle', {
          defaultValue: 'Could not join the exam room',
        })}
        message={t('oralExam.joinFailed')}
        retry={retry}
        leave={leaveSession}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }

  if (isRunStatePollError && !runState) {
    return (
      <OralExamErrorState
        title={t('oralExam.errorState.stateLoadFailedTitle', {
          defaultValue: 'Could not load exam state',
        })}
        message={t('oralExam.errorState.stateLoadFailedBody', {
          defaultValue: 'We lost contact with the server. Try again.',
        })}
        retry={() => { refetchRunState(); }}
        leave={leaveSession}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }

  const isTerminal = runState?.state === 'completed' || runState?.state === 'aborted';
  const isSessionExpired = runState?.operationalFault === OralExamOperationalFault.session_expired;
  const useMeetingStageSpeakerRail = Boolean(
    runState
    && runState.mode === OralExamMode.one_to_one
    && runState.sessionPhase === OralExamSessionPhase.in_exam,
  );
  // Deliberately NOT wrapped in MeetingSessionShell/MeetingRoom: hard faults
  // and wrap-up unmount LiveKit so cam/mic/screen release immediately. Terminal
  // 1:N exam sessions leave as soon as the last question is done — they do not wait
  // for the lecturer to close the cohort.
  if (runState && isSessionExpired && !isTerminal) {
    return <StudentSessionExpiredScreen onLeave={leaveSession} />;
  }

  if (runState && shouldLeaveMeeting) {
    if (shouldShowAgentUnavailableTerminal(runState)) {
      return (
        <AgentUnavailableTerminalScreen
          runState={runState}
          onStartDryRun={isSelfServiceRun(runState) ? enterPracticeLobby : undefined}
          isStartingDryRun={isEnteringPractice}
          startDryRunError={enterPracticeError}
          leave={leaveSession}
          leaveLabel={t('oralExam.leaveExam')}
        />
      );
    }
    const wrapUpState = runState.state === 'completed'
      ? 'completed'
      : runState.state === 'aborted'
        ? (runState.committed ? 'aborted' : 'not_started')
        : 'closed';
    return (
      <ExamWrapUp
        state={wrapUpState}
        recordingRequired={runState.recordingRequired}
        requiresEvaluation={runRequiresEvaluation(runState)}
        showPresence={runState.mode !== OralExamMode.one_to_one}
        onDone={() => handleWrapUpDone(wrapUpState)}
      />
    );
  }

  const connectingSpinner = (
    <Center flex={1} p="$4">
      <Spinner size="large" />
      <Text size="sm" color="$textSubtle" mt="$2">{t('oralExam.connecting')}</Text>
    </Center>
  );

  const isPreFlight = runState?.sessionPhase === OralExamSessionPhase.pre_flight;
  const isGroupBriefing = runState?.sessionPhase === OralExamSessionPhase.group_briefing;
  const isWaiting = runState?.state === 'pre_start';
  const idRejected = runState?.idVerification?.status === 'rejected';
  const leavingPreflightToBriefing = (
    prevPreflightPhaseRef.current === OralExamSessionPhase.pre_flight
    && isGroupBriefing
  );
  const holdPreflightShell = isPreFlight || showPreflightLeaveOverlay || leavingPreflightToBriefing;
  // Waiting bed follows the orientation-film cohort (incl. self-service dry-run),
  // not the lecturer-only group lounge. Hint carousel stays lounge-gated.
  const atmosphereActive = filmEnabled && holdPreflightShell && lobbyActiveStep === 'waiting' && !idRejected;
  const pastIdentityCapture = idVerificationStatus === 'submitted'
    || idVerificationStatus === 'approved';
  // Finish-first keeps the orientation film playing while ID is rejected — mute
  // that step regardless of id status so film audio cannot leak into LiveKit.
  const muteUplinkActive = holdPreflightShell && (
    (filmEnabled && lobbyActiveStep === 'orientation')
    || (filmEnabled && lobbyActiveStep === 'waiting' && !idRejected)
    || (
      filmEnabled
      && lobbyActiveStep == null
      && !runState?.orientationCompletedAt
      && pastIdentityCapture
      && !idRejected
    )
  );
  const atmosphereUnload = !filmEnabled || idRejected || !holdPreflightShell;

  const oralExamLiveView = runState ? (
    <ConnectionAwareOralExamLiveView
      runState={runState}
      driver={runState.driver}
      studentFirstName={studentFirstName}
      onStartExam={() => { handleStartExam().catch(() => undefined); }}
      isMarkingStudentReady={isMarkingStudentReady}
      startExamError={startExamError}
      studentDoneError={studentDoneError}
      onStudentDone={handleStudentDone}
      onOpenAnswerWindow={handleOpenAnswerWindow}
      onRequestHelp={isSelfServiceRun(runState)
        ? undefined
        : () => { requestHelp().catch(() => undefined); }}
      isSignalingDone={isSignalingDone}
      isRequestingHelp={isRequestingHelp}
      bookingId={ids.bookingId}
      routeIds={ids}
      supervisorName={isSelfServiceRun(runState)
        ? undefined
        : (frameSupervisorName ?? runState.supervisorName ?? undefined)}
      supervisorPhotoUrl={isSelfServiceRun(runState) ? null : frameSupervisorPhotoUrl}
      supervisorFaces={isSelfServiceRun(runState) ? [] : supervisorFaces}
      visitingLecturerId={isSelfServiceRun(runState) ? null : visitingLecturerId}
      onDone={leaveSession}
      onCompletedWrapUpDone={handleCompletedWrapUpDone}
      onAbort={handleAbortExam}
      isAborting={isAborting}
    />
  ) : null;

  // pre_flight → the unified Lobby (full-screen, with its own device step). Both
  // pre_start AND in_progress (and the terminal states) → OralExamStage via
  // OralExamLiveView. Cohort in-exam uses StudentExamShell (owns SupervisorFrame);
  // 1:1 in-progress uses OneToOneQuestionConsole.
  let sessionBody = connectingSpinner;
  if (runState && holdPreflightShell) {
    sessionBody = (
      <PreflightWaitingAtmosphere
        bedActive={atmosphereActive}
        muteUplink={muteUplinkActive}
        waitingBedUrl={loungeFetchSucceeded ? loungePayload.waitingBedUrl : null}
        unload={atmosphereUnload}
        showLeavingOverlay={showPreflightLeaveOverlay}
        hostMuteInBanner={isSelfServiceRun(runState)}
      >
        <OralExamLobby
          runState={runState}
          onRecordConsent={() => {
            recordConsent({
              policyVersion: ORAL_EXAM_CONSENT_POLICY_VERSION,
              scope: ORAL_EXAM_CONSENT_SCOPE,
            }).catch(() => undefined);
          }}
          onAcknowledgeConduct={() => {
            acknowledgeConduct({
              policyVersion: ORAL_EXAM_CONDUCT_POLICY_VERSION,
            }).catch(() => undefined);
          }}
          onSubmitIdentityPhotos={(documentImageBase64, selfieImageBase64) => submitIdentityPhotos({
            documentImageBase64,
            selfieImageBase64,
          })}
          isRecordingConsent={isRecordingConsent}
          isAcknowledgingConduct={isAcknowledgingConduct}
          isSubmittingIdentityPhotos={isSubmittingIdentityPhotos}
          onMarkPreflightLobbyReady={(readyEtag) => {
            markPreflightLobbyReady({ etag: readyEtag }).catch((error) => {
              if (!isSelfServiceRun(runState)) return;
              setStartPracticeError(getOralExamApiErrorMessage(
                error,
                t('oralExam.practice.startFailed', {
                  defaultValue: 'Could not start the dry run. Try again.',
                }),
              ));
            });
          }}
          onBack={handleLeavePreflight}
          loungeHints={loungeFetchSucceeded ? loungePayload.hints : []}
          useProductDefaultHints={groupLoungeEnabled}
          orientationVideoUrl={orientationVideoUrlForLobby}
          loungeOrientationResolved={loungeOrientationResolved}
          loungeFetchFailed={loungeFetchEnabled && loungeFetchFailed}
          onOrientationComplete={handleOrientationComplete}
          isCompletingOrientation={isCompletingOrientation}
          orientationStampRetrying={orientationStampRetrying}
          orientationStampExhausted={orientationStampExhausted}
          onActiveStepChange={setLobbyActiveStep}
          slotStartIso={slotStartIso}
          onStartPractice={isSelfServiceRun(runState) ? handleStartPractice : undefined}
          isStartingPractice={isStartingSelfService}
          startPracticeError={startPracticeError}
        />
      </PreflightWaitingAtmosphere>
    );
  } else if (runState && isGroupBriefing) {
    sessionBody = (
      <VStack flex={1}>
        <Box flex={1} minHeight={0}>
          <StudentGroupBriefingScreen
            slotId={slotId}
            brieferId={runState.brieferId}
            slotOwnerId={runState.slotOwnerId}
            onServerCheckRequested={() => { refetchRunState().catch(() => undefined); }}
          />
        </Box>
        {/* Non-destructive: leave the room (releases media) without ending the
            run — the student keeps their booking and can rejoin. */}
        <HStack justifyContent="center" py="$3" px="$4">
          <Button
            variant="outline"
            size="sm"
            action="secondary"
            onPress={leaveSession}
            testID="student-briefing-leave"
          >
            <ButtonText>{t('oralExam.leaveExam')}</ButtonText>
          </Button>
        </HStack>
      </VStack>
    );
  } else if (runState) {
    sessionBody = (
      <OralExamInRoomSession
        runState={runState}
        useMeetingStageSpeakerRail={useMeetingStageSpeakerRail}
        oralExamLiveView={oralExamLiveView}
        isTerminal={isTerminal}
        isWaiting={isWaiting}
        onBack={leaveSession}
        handleAbortExam={handleAbortExam}
        isAborting={isAborting}
        completeIfAbsent={completeIfAbsent}
      />
    );
  }

  if (openElsewhere) {
    return (
      <OralExamErrorState
        testID="meeting-open-elsewhere"
        retryTestID="meeting-open-elsewhere-use-here"
        tone="neutral"
        title={t('oralExam.openElsewhere.title', {
          defaultValue: 'This exam is open in another window.',
        })}
        retry={useThisWindow}
        retryLabel={t('oralExam.openElsewhere.useThisWindow', {
          defaultValue: 'Use this window',
        })}
        leave={leaveSession}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }

  return (
    <MeetingSessionShell
      key={`meeting-${sessionGeneration}`}
      token={joinCreds.token}
      serverUrl={joinCreds.livekitUrl}
      roomName={joinCreds.roomName}
      participantRole="student"
      lobbyDone={lobbyDone}
      joinSettings={effectiveJoinSettings}
      onLobbyJoin={onLobbyJoin}
      onLeave={leaveSession}
      onRejoin={retry}
      onConnected={clearInSessionTokenRetryWait}
      skipLeaveConfirm
      expectDisconnect={isTerminal}
    >
      {runState ? (
        <LiveKitConnectAwareSessionBody
          sessionBody={sessionBody}
          isRunStatePollError={isRunStatePollError}
          runState={runState}
          onBack={leaveSession}
          onRejoin={retry}
        />
      ) : connectingSpinner}
    </MeetingSessionShell>
  );
}

/**
 * Student oral exam session: meeting room, tiles, and mirrored run state.
 *
 * Wrapped in {@link CarriedScreenShareProvider} so the lobby's gesture-captured
 * screen share survives the pre-flight → exam room remount (see the provider for
 * the full rationale). The provider lives above every early return in the inner
 * component so its retained track is never dropped by a transient loading/refetch
 * render.
 */
export function OralExamLiveSession(props: OralExamLiveSessionProps) {
  return (
    <CarriedScreenShareProvider>
      <CarriedJoinSettingsProvider>
        <MeetingOpenElsewhereProvider stopOnRoomClosed={false}>
          <OralExamLiveSessionInner {...props} />
        </MeetingOpenElsewhereProvider>
      </CarriedJoinSettingsProvider>
    </CarriedScreenShareProvider>
  );
}
